import { Fragment, useEffect, useState, useRef } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import Select from "react-select";
import axios from "axios";
import { baseURL } from "../../../../Services/api/baseURL";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { H3 } from "../../../../AbstractElements";

// Matches flashDealController's action-based routes, mounted at /api/flash-deals.
const FLASH_DEAL_API = `${baseURL}/api/flash-deals`;

function CreateFlashDeal() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const [productOptions, setProductOptions] = useState([]);
  // Single product+variant pick, not multi — the backend model stores one
  // `product` + one `variant` per deal, not an array.
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [websiteBannerPreview, setWebsiteBannerPreview] = useState(null);
  const [websiteBannerFile, setWebsiteBannerFile] = useState(null);

  const [mobileBannerPreview, setMobileBannerPreview] = useState(null);
  const [mobileBannerFile, setMobileBannerFile] = useState(null);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [currentCropType, setCurrentCropType] = useState(null);
  const [imageToCrop, setImageToCrop] = useState(null);

  const [crop, setCrop] = useState({
    unit: "%",
    width: 90,
    aspect: 11 / 1,
  });

  const [completedCrop, setCompletedCrop] = useState(null);

  const imgRef = useRef(null);

  useEffect(() => {
    const t = JSON.parse(localStorage.getItem("token"));
    setToken(t);

    // Pass token directly rather than relying on state — setToken(t) above
    // hasn't committed yet on this same render pass.
    fetchProducts(t);

    if (id) {
      fetchFlashDeal(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    subtitle: Yup.string(),
    description: Yup.string(),

    product: Yup.string().required("Select a product"),
    variant: Yup.string().required("Select a product variant"),

    originalPrice: Yup.number()
      .required("Original price is required")
      .positive("Must be a positive number"),

    dealPrice: Yup.number()
      .required("Deal price is required")
      .positive("Must be a positive number")
      .test(
        "less-than-original",
        "This variant's store pricing has no discount set, so there's nothing to feature — add a discount to it in store pricing first, or pick a different variant.",
        function (value) {
          const { originalPrice } = this.parent;
          if (value === undefined || originalPrice === undefined) return true;
          return Number(value) < Number(originalPrice);
        }
      ),

    badge: Yup.string(),
    backgroundColor: Yup.string(),
    textColor: Yup.string(),
    buttonColor: Yup.string(),
    priority: Yup.number(),

    startDate: Yup.date().required(),
    endDate: Yup.date()
      .required()
      .min(Yup.ref("startDate"), "End Date must be after Start Date"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      subtitle: "",
      description: "",
      product: "",
      variant: "",
      originalPrice: "",
      dealPrice: "",
      badge: "",
      backgroundColor: "#1a1a1a",
      textColor: "#ffffff",
      buttonColor: "#d3178a",
      priority: 0,
      startDate: new Date(),
      endDate: new Date(),
      status: "active",
    },

    validationSchema,

    onSubmit: async (values) => {
      try {
        setLoading(true);

        const formData = new FormData();

        formData.append("title", values.title);
        formData.append("subtitle", values.subtitle);
        formData.append("description", values.description);
        formData.append("product", values.product);
        formData.append("variant", values.variant);
        formData.append("originalPrice", values.originalPrice);
        formData.append("dealPrice", values.dealPrice);
        formData.append("badge", values.badge);
        formData.append("backgroundColor", values.backgroundColor);
        formData.append("textColor", values.textColor);
        formData.append("buttonColor", values.buttonColor);
        formData.append("priority", values.priority);
        formData.append("startDate", values.startDate);
        formData.append("endDate", values.endDate);
        formData.append("status", values.status);

        if (websiteBannerFile) formData.append("websiteBanner", websiteBannerFile);
        if (mobileBannerFile) formData.append("mobileBanner", mobileBannerFile);

        let response;

        if (id) {
          response = await axios.patch(
            `${FLASH_DEAL_API}/update-flash-deal/${id}`,
            formData,
            {
              headers: {
                Authorization: token,
                "Content-Type": "multipart/form-data",
              },
            }
          );
        } else {
          response = await axios.post(`${FLASH_DEAL_API}/add-flash-deal`, formData, {
            headers: {
              Authorization: token,
              "Content-Type": "multipart/form-data",
            },
          });
        }

        Swal.fire({
          icon: "success",
          title: response.data.message,
        });

        navigate("/flash-deals");
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: err?.response?.data?.message || "Something went wrong",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  // =========================
  // Fetch Product Options
  // =========================

  const fetchProducts = async (authToken) => {
    try {
      const response = await axios.get(`${FLASH_DEAL_API}/get-products`, {
        headers: { Authorization: authToken ?? token },
      });

      if (response.data.success) {
        setProductOptions(response.data.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // =========================
  // Fetch Existing Flash Deal
  // =========================

  const cleanVariantLabel = (rawLabel = "") => {
    const variantPart = rawLabel.includes(" - ")
      ? rawLabel.split(" - ").pop()
      : rawLabel;

    return variantPart.replace(/\s*\(\d+\)\s*$/, "").trim();
  };

  const fetchFlashDeal = async (authToken) => {
    try {
      const response = await axios.get(`${FLASH_DEAL_API}/get-flash-deal/${id}`, {
        headers: { Authorization: authToken ?? token },
      });

      if (!response.data.success) return;

      const data = response.data.data;

      formik.setValues({
        title: data.title || "",
        subtitle: data.subtitle || "",
        description: data.description || "",
        product: data.product?._id || "",
        variant: data.variant?._id || "",
        originalPrice: data.originalPrice ?? "",
        dealPrice: data.dealPrice ?? "",
        badge: data.badge || "",
        backgroundColor: data.backgroundColor || "#1a1a1a",
        textColor: data.textColor || "#ffffff",
        buttonColor: data.buttonColor || "#d3178a",
        priority: data.priority ?? 0,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        status: data.status || "active",
      });

      // if (data.product && data.variant) {
      //   setSelectedProduct({
      //     value: data.variant._id,
      //     productId: data.product._id,
      //     label: `${data.product.productName} - ${data.variant.variantName}`,
      //   });
      // }
      if (data.product && data.variant) {
        setSelectedProduct({
          value: data.variant._id,
          productId: data.product._id,
          label: cleanVariantLabel(data.variant.variantName),
        });
      }

      if (data.websiteBanner) {
        setWebsiteBannerPreview(data.websiteBanner);
      }

      if (data.mobileBanner) {
        setMobileBannerPreview(data.mobileBanner);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // =========================
  // Product Selection (single)
  // =========================

  const handleProductChange1 = (option) => {
    setSelectedProduct(option);
    formik.setFieldValue("product", option?.productId || "");
    formik.setFieldValue("variant", option?.value || "");
  };
  const handleProductChange2 = (option) => {
    setSelectedProduct(option);
    formik.setFieldValue("product", option?.productId || "");
    formik.setFieldValue("variant", option?.value || "");

    // Backend already resolves finalSellingPrice ?? sellingPrice into `price`
    formik.setFieldValue("originalPrice", option?.price ?? "");
  };
  const handleProductChange = (option) => {
    setSelectedProduct(option);
    formik.setFieldValue("product", option?.productId || "");
    formik.setFieldValue("variant", option?.value || "");

    formik.setFieldValue("originalPrice", option?.sellingPrice ?? "");
    formik.setFieldValue("dealPrice", option?.finalSellingPrice ?? "");
  };

  // =========================
  // Image Validation
  // =========================

  const validateImageAspectRatio = (file, ratio, type) => {
    return new Promise((resolve) => {
      const img = new Image();

      img.onload = function () {
        const currentRatio = this.width / this.height;

        if (Math.abs(currentRatio - ratio) > 0.1) {
          Swal.fire({
            icon: "warning",
            title: "Invalid Aspect Ratio",
            text: "Would you like to crop the image?",
            showCancelButton: true,
            confirmButtonText: "Crop",
          }).then((result) => {
            if (result.isConfirmed) {
              openCropModal(file, type, ratio);
            }
          });

          resolve(false);
        } else {
          resolve(true);
        }
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // =========================
  // Website Banner
  // =========================

  const handleWebsiteBanner = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("Image size should be below 5MB");
      return;
    }

    const valid = await validateImageAspectRatio(file, 11, "website");
    if (!valid) return;

    setWebsiteBannerFile(file);
    setWebsiteBannerPreview(URL.createObjectURL(file));
  };

  // =========================
  // Mobile Banner
  // =========================

  const handleMobileBanner = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("Image size should be below 5MB");
      return;
    }

    const valid = await validateImageAspectRatio(file, 16 / 9, "mobile");
    if (!valid) return;

    setMobileBannerFile(file);
    setMobileBannerPreview(URL.createObjectURL(file));
  };

  // =========================
  // Crop Modal
  // =========================

  const openCropModal = (file, type, aspect) => {
    setCurrentCropType(type);
    setCrop({ unit: "%", width: 90, aspect });
    setImageToCrop(URL.createObjectURL(file));
    setCropModalOpen(true);
  };

  const closeCropModal = () => {
    setCropModalOpen(false);
    setImageToCrop(null);
    setCompletedCrop(null);
    // reactstrap Modal + SweetAlert2 can strand a scroll-lock on <body>/<html>
    // when they close in overlapping ticks — force-clear it every time.
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.body.classList.remove("modal-open");
    document.documentElement.classList.remove("swal2-height-auto", "swal2-shown");
  };

  const onImageLoad = (e) => {
    imgRef.current = e.currentTarget;
  };

  // =========================
  // Remove Images
  // =========================

  const removeWebsiteBanner = () => {
    setWebsiteBannerPreview(null);
    setWebsiteBannerFile(null);
  };

  const removeMobileBanner = () => {
    setMobileBannerPreview(null);
    setMobileBannerFile(null);
  };

  // ==========================================
  // Generate Cropped Image
  // ==========================================

  const generateCroppedImage = async () => {
    if (!completedCrop || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) return;
          const file = new File([blob], `flashdeal-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          resolve(file);
        },
        "image/jpeg",
        0.95
      );
    });
  };

  // ==========================================
  // Apply Crop
  // ==========================================

  const handleCropComplete = async () => {
    const croppedImage = await generateCroppedImage();

    if (!croppedImage) {
      Swal.fire({ icon: "error", title: "Please crop image first" });
      return;
    }

    if (currentCropType === "website") {
      setWebsiteBannerFile(croppedImage);
      setWebsiteBannerPreview(URL.createObjectURL(croppedImage));
    }

    if (currentCropType === "mobile") {
      setMobileBannerFile(croppedImage);
      setMobileBannerPreview(URL.createObjectURL(croppedImage));
    }

    closeCropModal();

    Swal.fire({
      icon: "success",
      title: "Image Cropped Successfully",
      timer: 1200,
      showConfirmButton: false,
    });
  };

  // ==========================================
  // Reset Form
  // ==========================================

  const handleReset = () => {
    formik.resetForm();
    setSelectedProduct(null);
    setWebsiteBannerPreview(null);
    setWebsiteBannerFile(null);
    setMobileBannerPreview(null);
    setMobileBannerFile(null);
  };

  // ==========================================
  // Theme Colors (for the react-select control itself)
  // ==========================================

  const isDarkMode = localStorage.getItem("mix_background_layout") === "dark-only";

  const uiColors = {
    background: isDarkMode ? "#262932" : "#ffffff",
    border: isDarkMode ? "#3f444d" : "#d9d9d9",
    text: isDarkMode ? "#ffffff" : "#000000",
    primary: "#0d6efd",
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: uiColors.background,
      borderColor: uiColors.border,
      minHeight: 42,
    }),
    menu: (provided) => ({ ...provided, backgroundColor: uiColors.background }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? uiColors.primary : uiColors.background,
      color: state.isFocused ? "#fff" : uiColors.text,
    }),
    input: (provided) => ({ ...provided, color: uiColors.text }),
    singleValue: (provided) => ({ ...provided, color: uiColors.text }),
  };

  // Live preview of the card, matching the reference design.
  const previewSavings =
    formik.values.originalPrice && formik.values.dealPrice
      ? (Number(formik.values.originalPrice) - Number(formik.values.dealPrice)).toFixed(2)
      : null;

  return (
    <Fragment>
      <Form onSubmit={formik.handleSubmit}>
        <Container fluid>
          <div className="page-title">
            <Row>
              <Col xs="6">
                <H3>{id ? "Update Flash Deal" : "Create Flash Deal"}</H3>
              </Col>

              <Col xs="6" className="text-end">
                {!id && (
                  <Button type="button" color="secondary" className="me-2" onClick={handleReset}>
                    Reset
                  </Button>
                )}

                <Button color="primary" disabled={loading}>
                  {loading ? "Saving..." : id ? "Update Flash Deal" : "Save Flash Deal"}
                </Button>
              </Col>
            </Row>
          </div>

          <Row>
            <Col lg="8">
              <Card>
                <CardBody>
                  <Row>
                    <Col md={8}>
                      <FormGroup>
                        <Label>
                          Title <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="title"
                          value={formik.values.title}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder='e.g. "Deal of the Day — Ends Tonight"'
                        />
                        {formik.touched.title && formik.errors.title && (
                          <small className="text-danger">{formik.errors.title}</small>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup>
                        <Label>Badge</Label>
                        <Input
                          name="badge"
                          value={formik.values.badge}
                          onChange={formik.handleChange}
                          placeholder='e.g. "SAVE $15"'
                        />
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <FormGroup>
                        <Label>Subtitle</Label>
                        <Input
                          name="subtitle"
                          value={formik.values.subtitle}
                          onChange={formik.handleChange}
                          placeholder='e.g. "Budweiser Magnum 12-Pack 500mL"'
                        />
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <FormGroup>
                        <Label>Product / Variant</Label>
                        <Select
                          styles={customStyles}
                          options={productOptions}
                          value={selectedProduct}
                          onChange={handleProductChange}
                          placeholder="Choose a product variant"
                          formatOptionLabel={(option) => cleanVariantLabel(option.label)}
                        />

                        {formik.touched.variant && formik.errors.variant && (
                          <small className="text-danger">{formik.errors.variant}</small>
                        )}
                      </FormGroup>
                    </Col>

                    {/* <Col md={6}>
                      <FormGroup>
                        <Label>
                          Original Price <span className="text-danger">*</span>
                        </Label>
                        
                        <Input
  name="originalPrice"
  type="number"
  step="0.01"
  value={formik.values.originalPrice}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  disabled={!selectedProduct}
/>
                        {formik.touched.originalPrice && formik.errors.originalPrice && (
                          <small className="text-danger">{formik.errors.originalPrice}</small>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>
                          Deal Price <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="dealPrice"
                          type="number"
                          step="0.01"
                          value={formik.values.dealPrice}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.dealPrice && formik.errors.dealPrice && (
                          <small className="text-danger">{formik.errors.dealPrice}</small>
                        )}
                        {previewSavings && !formik.errors.dealPrice && (
                          <small className="text-success d-block mt-1">
                            Save ${previewSavings}
                          </small>
                        )}
                      </FormGroup>
                    </Col> */}

                    <Col md={6}>
                      <FormGroup>
                        <Label>Original Price</Label>
                        <Input
                          name="originalPrice"
                          type="number"
                          step="0.01"
                          value={formik.values.originalPrice}
                          readOnly
                          disabled={!selectedProduct}
                        />
                        <small className="text-muted d-block mt-1">
                          From this product's store pricing — not editable.
                        </small>
                        {formik.touched.originalPrice && formik.errors.originalPrice && (
                          <small className="text-danger">{formik.errors.originalPrice}</small>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>Deal Price</Label>
                        <Input
                          name="dealPrice"
                          type="number"
                          step="0.01"
                          value={formik.values.dealPrice}
                          readOnly
                          disabled={!selectedProduct}
                        />
                        <small className="text-muted d-block mt-1">
                          From this product's store pricing — not editable.
                        </small>
                        {formik.touched.dealPrice && formik.errors.dealPrice && (
                          <small className="text-danger">{formik.errors.dealPrice}</small>
                        )}
                        {previewSavings && !formik.errors.dealPrice && (
                          <small className="text-success d-block mt-1">Save ${previewSavings}</small>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>Start Date</Label>
                        <DatePicker
                          className="form-control"
                          selected={formik.values.startDate}
                          onChange={(date) => formik.setFieldValue("startDate", date)}
                          showTimeSelect
                          dateFormat="dd/MM/yyyy h:mm aa"
                        />
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>End Date</Label>
                        <DatePicker
                          className="form-control"
                          selected={formik.values.endDate}
                          onChange={(date) => formik.setFieldValue("endDate", date)}
                          showTimeSelect
                          dateFormat="dd/MM/yyyy h:mm aa"
                          minDate={formik.values.startDate}
                        />
                      </FormGroup>
                    </Col>

                    <Col md={12}>
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          rows={4}
                          type="textarea"
                          name="description"
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          placeholder="Description..."
                        />
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>Website Banner (11:1)</Label>
                        <Input type="file" accept="image/*" onChange={handleWebsiteBanner} />

                        {websiteBannerPreview && (
                          <div className="mt-3">
                            <img
                              src={websiteBannerPreview}
                              alt=""
                              style={{
                                width: "100%",
                                aspectRatio: "11/1",
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                            <Button
                              type="button"
                              color="danger"
                              size="sm"
                              className="mt-2"
                              onClick={removeWebsiteBanner}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>Mobile Banner (16:9)</Label>
                        <Input type="file" accept="image/*" onChange={handleMobileBanner} />

                        {mobileBannerPreview && (
                          <div className="mt-3">
                            <img
                              src={mobileBannerPreview}
                              alt=""
                              style={{
                                width: "100%",
                                aspectRatio: "16/9",
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                            <Button
                              type="button"
                              color="danger"
                              size="sm"
                              className="mt-2"
                              onClick={removeMobileBanner}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup>
                        <Label>Priority</Label>
                        <Input
                          name="priority"
                          type="number"
                          value={formik.values.priority}
                          onChange={formik.handleChange}
                        />
                        <small className="text-muted">
                          Lower number wins when multiple deals overlap.
                        </small>
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup>
                        <Label>Status</Label>
                        <Input
                          type="select"
                          name="status"
                          value={formik.values.status}
                          onChange={formik.handleChange}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Styling + live preview */}
            <Col lg="4">
              <Card>
                <CardBody>
                  <h6 className="mb-3">Card Styling</h6>

                  <FormGroup>
                    <Label>Background Color</Label>
                    <Input
                      type="color"
                      name="backgroundColor"
                      value={formik.values.backgroundColor}
                      onChange={formik.handleChange}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Text Color</Label>
                    <Input
                      type="color"
                      name="textColor"
                      value={formik.values.textColor}
                      onChange={formik.handleChange}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Button Color</Label>
                    <Input
                      type="color"
                      name="buttonColor"
                      value={formik.values.buttonColor}
                      onChange={formik.handleChange}
                    />
                  </FormGroup>

                  <hr />

                  <h6 className="mb-3">Preview</h6>
                  <div
                    style={{
                      background: formik.values.backgroundColor,
                      color: formik.values.textColor,
                      borderRadius: 16,
                      padding: 20,
                    }}
                  >
                    {formik.values.badge && (
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 1,
                          color: formik.values.buttonColor,
                          marginBottom: 8,
                        }}
                      >
                        {formik.values.badge}
                      </div>
                    )}
                    <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.3 }}>
                      {formik.values.title || "Deal Title"}
                    </div>
                    <div style={{ fontSize: 13, opacity: 0.8, marginTop: 6 }}>
                      {formik.values.subtitle || selectedProduct?.label || "Subtitle"}
                    </div>
                    <div style={{ marginTop: 14, display: "flex", alignItems: "baseline", gap: 8 }}>
                      {formik.values.originalPrice && (
                        <span style={{ textDecoration: "line-through", opacity: 0.5, fontSize: 14 }}>
                          ${Number(formik.values.originalPrice).toFixed(2)}
                        </span>
                      )}
                      <span style={{ fontSize: 24, fontWeight: 800, color: formik.values.buttonColor }}>
                        {formik.values.dealPrice
                          ? `$${Number(formik.values.dealPrice).toFixed(2)}`
                          : "$0.00"}
                      </span>
                    </div>
                    <button
                      type="button"
                      style={{
                        marginTop: 16,
                        width: "100%",
                        border: "none",
                        borderRadius: 999,
                        padding: "10px 16px",
                        background: formik.values.buttonColor,
                        color: "#fff",
                        fontWeight: 600,
                      }}
                    >
                      Add to cart
                    </button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Form>

      <Modal isOpen={cropModalOpen} toggle={closeCropModal} size="lg" centered onClosed={closeCropModal}>
        <ModalHeader toggle={closeCropModal}>Crop Image</ModalHeader>

        <ModalBody>
          {imageToCrop && (
            <ReactCrop
              crop={crop}
              aspect={crop.aspect}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
            >
              <img src={imageToCrop} ref={imgRef} onLoad={onImageLoad} alt="" style={{ maxWidth: "100%" }} />
            </ReactCrop>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={closeCropModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleCropComplete}>
            Apply Crop
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
}

export default CreateFlashDeal;
